import React, { useCallback, useEffect, useState } from 'react';
import {
  DropdownButton,
  Offcanvas,
  Container,
  Dropdown,
  Button,
  Navbar,
  Nav,
  Col,
  Row
} from 'react-bootstrap';
import {
  AccountCircleOutlined,
  MenuOutlined,
  InfoOutlined
} from '@material-ui/icons';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { useLocalization } from '../contexts/LocalizationContext';
import { useElementSize } from '../hooks/useElementSize';
import { useAuth } from '../contexts/AuthContext';

import NavigationLangSelection from './NavigationLangSelection';
import NavigationUserDropdown from './NavigationUserDropdown';
import NavigationPagemenu from './NavigationPagemenu';
import NavigationToolbar from './NavigationToolbar';

import { getBaseRoute } from '../utility';

import ainiLogo from '../../public/assets/img/Eduplan_Aini_logo_icon_orange.svg';
import styles from './Navigation.module.scss';

const imageStyle = {
  transform: 'scale(1)',
  width: '3rem',
  outlineOffset: '-5px'
};

const mobileBreakpoint = 992;
const instructions = window._env.urls.manual;

export default function Navigation() {
  const { user, role, selectedTenant, changeSessionTenant } = useAuth();

  const { localize } = useLocalization();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [show, setShow] = useState(false);
  const [pageRef, page] = useElementSize();

  const roleName = selectedTenant?.roles?.find((r) => r?.key === role);
  const link = instructions && roleName ? instructions[roleName?.key] : null;

  useEffect(() => {
    if (!page.width) return;

    if (page.width >= mobileBreakpoint) {
      setShow(false);
    }
  }, [page.width]);

  useEffect(() => {
    setShow(false);
  }, [location.pathname]);

  const handleTenantChange = useCallback(
    async (id) => {
      if (!selectedTenant || selectedTenant.id === id) return;
      const org = await changeSessionTenant(id);

      const path = org.tenants?.find((t) => t.id === org.selectedTenant).path;
      navigate(`/admin/${path}` + location.search, { replace: true });
      window.location.reload();
    },
    [selectedTenant]
  );

  return (
    <>
      <Helmet>
        <title>{`${localize('common.pageHeader', { editable: false })} ${
          params['*'] ? ' - ' + params['*'].path : ''
        } | ${localize('common.appName', { editable: false })}`}</title>
      </Helmet>
      <Navbar
        expand='lg'
        expanded={show}
        className={styles.navigation}
        ref={pageRef}>
        <Container fluid='lg'>
          <Navbar.Brand
            as={Link}
            to={getBaseRoute(user, role)}
            style={imageStyle}
            aria-label={localize('nav.home', { editable: false })}>
            <img src={ainiLogo} alt='' />
          </Navbar.Brand>
          <Navbar.Toggle
            className={styles.toggle}
            label={localize('nav.show')}
            onClick={() => setShow((prev) => !prev)}
          />
          <Navbar.Offcanvas placement='end' onHide={() => setShow(false)}>
            <Offcanvas.Header closeButton closeLabel={localize('common.close', { editable: false })}>
              <span>
                <AccountCircleOutlined /> {user?.firstNames} {user?.lastName}
              </span>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <div className='d-flex flex-column flex-lg-row align-items-center justify-content-between w-100 h-100'>
                <div className='w-100'>
                  <NavigationPagemenu />
                </div>
                <div className='d-flex flex-column flex-lg-row align-items-center justify-content-end gap-2'>
                  {selectedTenant && (
                    <Col className='d-flex justify-content-end'>
                      <div role='toolbar' style={{ alignSelf: 'center' }}>
                        <Col className='d-flex justify-content-end'>
                          <div role='toolbar' style={{ alignSelf: 'center' }}>
                            {user?.roles?.find((r) => r.level === 0) &&
                              user?.tenants && (
                                <>
                                  <DropdownButton
                                    id='dropdown-basic-button'
                                    variant='outline-primary'
                                    title={localize(selectedTenant?.name)}>
                                    {user?.tenants?.map((item) => {
                                      return (
                                        <Dropdown.Item
                                          key={item.id}
                                          as={'button'}
                                          onClick={() =>
                                            handleTenantChange(item.id)
                                          }>
                                          {localize(item.name)}
                                        </Dropdown.Item>
                                      );
                                    })}
                                  </DropdownButton>
                                </>
                              )}
                          </div>
                        </Col>
                      </div>
                    </Col>
                  )}
                  {link && localize(link) && (
                    <Nav.Link
                      target='_blank'
                      rel='noopener noreferrer'
                      href={localize(link)}
                      aria-label={localize('common.info', { editable: false })}>
                      <InfoOutlined aria-hidden='true' />
                    </Nav.Link>
                  )}
                  <NavigationLangSelection />
                  <NavigationUserDropdown />
                </div>
              </div>
            </Offcanvas.Body>
          </Navbar.Offcanvas>
        </Container>
      </Navbar>
    </>
  );
}

export function AdminNav({ handleShow }) {
  const { user, role } = useAuth();
  const { localize } = useLocalization();

  return (
    <Navbar>
      <Container fluid>
        <Link
          style={{ height: '2rem', width: '2rem', display: 'flex' }}
          className='navbar-brand p-0'
          to={getBaseRoute(user, role)}
          aria-label={localize('nav.home', { editable: false })}>
          <img src='/assets/img/hali-logo.svg' alt='' />
        </Link>
        <div className='d-block d-lg-none' style={{ width: '100%' }}>
          <Button
            onClick={handleShow}
            className='ms-auto '
            variant='outline-secondary'>
            <MenuOutlined />{' '}
            <span className='visually-hidden'>{localize('common.menu')}</span>
          </Button>
        </div>

        <div className='d-none d-lg-block' style={{ width: '100%' }}>
          <Nav style={{ width: '100%' }}>
            <NavigationPagemenu />

            <div className='d-flex ms-lg-auto'>
              <NavigationLangSelection />

              <NavigationUserDropdown />
            </div>
          </Nav>
        </div>
      </Container>
    </Navbar>
  );
}

export function StudentNav() {
  const { user, role } = useAuth();
  const { localize } = useLocalization();

  return (
    <Container fluid>
      <Row>
        <Col>
          <Navbar expand='lg' className='gap-4 gap-lg-2'>
            <Link
              style={{ height: '2rem', width: '2rem', display: 'flex' }}
              className='navbar-brand p-0'
              to={getBaseRoute(user, role)}
              aria-label={localize('nav.home', { editable: false })}>
              <img src='/assets/img/hali-logo.svg' alt='' />
            </Link>
            <div
              role='toolbar'
              className='yah-toolbar d-flex d-lg-none col mr-auto justify-content-between'>
              <NavigationToolbar />
            </div>
            <Navbar.Toggle
              aria-controls='navbarToggler'
              label={localize('nav.show')}
            />

            <Navbar.Collapse className='gap-2' id='navbarScroll'>
              <NavigationPagemenu />
              <div className='d-flex my-2 my-lg-0 gap-2 align-items-center flex-column flex-lg-row'>
                <NavigationLangSelection />
                <NavigationUserDropdown />
              </div>
            </Navbar.Collapse>
          </Navbar>
        </Col>
      </Row>
    </Container>
  );
}
